import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraKeyValueRow, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentPreviewDialog } from '../_shared/components/DocumentPreviewDialog';
import { CmDrawer } from '../Deliverables/components/CmDrawer';
import { CmStatusBadge } from '../Deliverables/components/common';
import { historyLines, SubmissionItem } from '../Deliverables/components/OccurrenceDrawer';
import { useOccurrence } from '../Deliverables/hooks';
import { cmDate, cmMonth } from '../Deliverables/lib/format';
import { occurrenceStatus } from '../Deliverables/lib/status';
import { useUserNames } from '../Deliverables/lookups';
import { remindableState } from './attention';
import { dueWords } from './listView';
import { RemindAction } from './NeedsAttention';
// SAD-1000: the drill-down list's side panel. It shows one date's details,
// routing and history from the row the list already read and the date's own
// GetOccurrence (the read the deliverable drawer makes), and nothing else:
// no per-team or per-deliverable plan, which the program pages read. Every
// action but Remind (a date still owed reminds its owner) stays on the
// date's own drawer, one click away ("Open deliverable").
const NO_FILES = new Map();
export function PortfolioOccurrencePanel({ programId, occId, row, programName, today, onClose }) {
    const navigate = useNavigate();
    const q = useOccurrence(programId, occId);
    const names = useUserNames();
    const [preview, setPreview] = useState(null);
    const occ = q.data?.occurrence ?? row?.occurrence;
    const submissions = useMemo(()=>[
            ...q.data?.submissions ?? []
        ].sort((a, b)=>b.attempt - a.attempt), [
        q.data?.submissions
    ]);
    const decisions = q.data?.decisions ?? [];
    const openHref = `/delivery/programs/${programId}/deliverables?occ=${encodeURIComponent(occId)}`;
    if (q.error && !occ) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            onClose: onClose,
            testId: Testids.commandCenter.panel,
            title: "Deliverable date",
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: onClose,
                    variant: "secondary",
                    children: "Close"
                }),
                description: "It may have been retired by a schedule change, or you may no longer have access to its program.",
                title: "This date could not be opened"
            })
        });
    }
    if (!occ) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            onClose: onClose,
            testId: Testids.commandCenter.panel,
            title: "Deliverable date",
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Loading…"
            })
        });
    }
    const status = occurrenceStatus(occ, row?.finalReviewTier, today);
    const due = dueWords(occ.dueOn, today);
    const title = row?.dutyTitle || 'Deliverable';
    // The date's period as its own drawer words it ("October 2026").
    const period = occ.label || cmMonth(occ.nominalDueOn || occ.dueOn);
    const program = row?.programName || programName || 'Program';
    const lines = historyLines(occ, decisions, names, '');
    const late = occ.status?.state === 'late';
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        description: period,
        eyebrow: /*#__PURE__*/ _jsx("span", {
            "data-testid": Testids.commandCenter.panelEyebrow,
            children: [
                program,
                row ? row.workstreamName || 'Contract-wide' : ''
            ].filter(Boolean).join(' · ')
        }),
        footerProps: {
            leading: row && remindableState(occ.status?.state) ? /*#__PURE__*/ _jsx(RemindAction, {
                label: row.ownerName ? `Remind ${row.ownerName}` : 'Remind',
                lastRemindedAt: row.lastRemindedAt,
                occurrenceId: occId,
                ownerName: row.ownerName,
                programId: programId,
                remindTestId: Testids.commandCenter.panelRemind,
                remindedTestId: Testids.commandCenter.panelReminded
            }) : undefined,
            cancelLabel: 'Back to the list',
            onCancel: onClose,
            confirmLabel: 'Open deliverable',
            onConfirm: ()=>navigate(openHref),
            confirmProps: {
                'data-testid': Testids.commandCenter.panelOpen
            }
        },
        onClose: onClose,
        testId: Testids.commandCenter.panel,
        title: title,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: status
                            }),
                            row?.atRisk ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "warning",
                                children: "At risk"
                            }) : null,
                            row?.carriedOver ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                children: "Carried over"
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                headingAs: "h3",
                                title: "Details",
                                variant: "group"
                            }),
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Due",
                                value: /*#__PURE__*/ _jsxs("span", {
                                    className: late ? 'text-danger' : undefined,
                                    children: [
                                        cmDate(occ.dueOn, 'No due date'),
                                        due.rel ? ` · ${due.rel}` : ''
                                    ]
                                })
                            }),
                            occ.movedFromDueOn ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Moved from",
                                value: cmDate(occ.movedFromDueOn)
                            }) : null,
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Program",
                                value: program
                            }),
                            row ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Team",
                                value: row.workstreamName || 'Contract-wide, no team'
                            }) : null
                        ]
                    }),
                    row ? /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                headingAs: "h3",
                                title: "Routing",
                                variant: "group"
                            }),
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Owner",
                                value: row.ownerContactId === '-' || !row.ownerName ? 'No owner: the team has none and the program no lead' : row.ownerName
                            }),
                            row.waitingSince ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Waiting since",
                                value: `${cmDate(row.waitingSince)} · ${dueWords(row.waitingSince, today).rel}`
                            }) : null
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-1.5",
                        "data-testid": Testids.commandCenter.panelFilings,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                headingAs: "h3",
                                title: "Filings",
                                variant: "group"
                            }),
                            q.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                                size: "caption",
                                tone: "muted",
                                children: "Loading…"
                            }) : submissions.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "body-ui",
                                tone: "muted",
                                children: "Nothing has been filed for this date yet."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-3",
                                children: submissions.map((s, i)=>/*#__PURE__*/ _jsx(SubmissionItem, {
                                        artifactNames: NO_FILES,
                                        latest: i === 0,
                                        onPreview: setPreview,
                                        s: s,
                                        statusLabel: i === 0 ? status.label : undefined,
                                        statusTone: status.tone
                                    }, s.id))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-1.5",
                        "data-testid": Testids.commandCenter.panelHistory,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                headingAs: "h3",
                                title: "History",
                                variant: "group"
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-1",
                                children: lines.map((line)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraText, {
                                            size: "body-ui",
                                            children: line
                                        })
                                    }, line))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: preview?.artifactId ?? '',
                onOpenChange: (o)=>{
                    if (!o) setPreview(null);
                },
                open: !!preview,
                title: preview?.name ?? 'Submission file'
            })
        ]
    });
}
